import { formatDate, formatPhone, type Lead } from "@elektr/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Phone, Search, ShoppingCart } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ExportButton } from "@/components/ExportButton";
import { Button } from "@/components/ui/button";
import { Badge, Card, EmptyState, PageHeader, Spinner } from "@/components/ui/card";
import { Input, Select } from "@/components/ui/form";
import { Pagination } from "@/components/ui/pagination";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { LEAD_STATUS_KEYS, LEAD_STATUS_TONE } from "@/lib/catalog";
import { useDebounced } from "@/lib/hooks";
import { useT } from "@/lib/i18n";

const PAGE_SIZE = 20;
const STATUSES = ["new", "contacted", "sold", "rejected"] as const;

/** Mijozlar mahsulotlar bo'yicha qoldirgan so'rovlar: xodim bog'lanadi va holatini yangilaydi. */
export function LeadsPage() {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [text, setText] = useState("");
  const q = useDebounced(text);
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ["leads", { status, q, page }],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/leads/", {
          params: {
            query: { status: status || undefined, q: q || undefined, page, page_size: PAGE_SIZE },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const update = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: { status?: Lead["status"]; note?: string };
    }) => unwrap(await api.PATCH("/api/leads/{id}/", { params: { path: { id } }, body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["leads"] }),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  const rows = list.data?.results ?? [];

  return (
    <>
      <PageHeader
        title={t("nav.leads")}
        actions={<ExportButton kind="leads" params={status ? { status } : {}} />}
      />
      <Card className="mb-4 grid gap-3 sm:grid-cols-[1fr_14rem]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3.5 size-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={t("lead.search")}
            aria-label={t("lead.search")}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <Select
          aria-label={t("props.status")}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t("props.all_statuses")}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(LEAD_STATUS_KEYS[s])}
            </option>
          ))}
        </Select>
      </Card>

      {list.isLoading && <Spinner className="m-8 size-8" />}
      {list.data && rows.length === 0 && <EmptyState>{t("lead.empty")}</EmptyState>}
      <div className="flex flex-col gap-3">
        {rows.map((l) => {
          const st = (l.status ?? "new") as (typeof STATUSES)[number];
          return (
            <Card key={l.id} className="flex flex-col gap-2" data-testid="lead-card">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{l.name || formatPhone(l.phone)}</div>
                  <a
                    className="inline-flex items-center gap-1 text-sm text-primary underline"
                    href={`tel:${l.phone}`}
                  >
                    <Phone className="size-3.5" /> {formatPhone(l.phone)}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{formatDate(l.created_at)}</span>
                  <Badge tone={LEAD_STATUS_TONE[st]}>{t(LEAD_STATUS_KEYS[st])}</Badge>
                </div>
              </div>
              {l.product_name && (
                <div className="text-sm">
                  {t("lead.product")}:{" "}
                  {l.product ? (
                    <Link
                      className="font-medium text-primary underline"
                      to={`/catalog/${l.product}`}
                    >
                      {l.product_name}
                    </Link>
                  ) : (
                    l.product_name
                  )}
                </div>
              )}
              {l.comment && <p className="rounded-lg bg-muted p-2 text-sm">{l.comment}</p>}
              <div className="flex flex-wrap items-center gap-2">
                {STATUSES.filter((s) => s !== st).map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={s === "sold" ? "primary" : "outline"}
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: l.id, body: { status: s } })}
                  >
                    {t(LEAD_STATUS_KEYS[s])}
                  </Button>
                ))}
                <Button asChild size="sm" variant="ghost">
                  <Link to={`/sales?lead=${l.id}`}>
                    <ShoppingCart className="size-4" /> {t("lead.record_sale")}
                  </Link>
                </Button>
              </div>
              <Input
                aria-label={t("lead.note")}
                placeholder={t("lead.note")}
                maxLength={500}
                defaultValue={l.note}
                onBlur={(e) =>
                  e.target.value !== l.note &&
                  update.mutate({ id: l.id, body: { note: e.target.value } })
                }
              />
            </Card>
          );
        })}
      </div>
      <Pagination page={page} count={list.data?.count ?? 0} pageSize={PAGE_SIZE} onPage={setPage} />
    </>
  );
}
